// Hotel Staff Hub (Phase C) — one hub, capability-gated.
// Entry: shared hallway card OR individual employee card → existing 4-digit
// staff PIN (or existing portal/My Tap login) → short-lived staff session
// token (in memory ONLY — shared-card safe) → authorized functions.
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
    BedDouble, ConciergeBell, BarChart3, Loader2, LockKeyhole, LogOut, ChevronLeft,
    ShieldCheck, CarFront, Building2, Wrench, CircleParking, Recycle, Waves, ClipboardList, ChevronRight,
} from 'lucide-react';
import { LanguageGate, ChangeLanguageLink } from '../../components/shared/LanguageGate';
import { STAFF_TEXT } from './staffText';
import { HousekeepingView, QueueView, ManagerView, ValetView, FloorView, RoomStatusView, RoomDetailsPanel } from './StaffScreens';
import { captureDemoToken, getDemoToken } from '../../lib/demoMode';
import ActionHistoryPanel from '../../components/shared/ActionHistoryPanel';
import { actionHistoryAPI } from '../../lib/api';
import { getToken } from '../../lib/session';

const API = typeof window !== 'undefined' ? window.location.origin : '';
const LANGS = ['en', 'fr', 'es'];
const LANG_KEY = 'yulontap_staff_lang';
const INACTIVITY_MS = 3 * 60 * 1000;

// Presentation metadata for each work area (icon + accent). Titles and plain
// descriptions come from `tx`. The hub only shows areas the person is
// authorized for, so nobody needs to know the underlying routing.
const AREA_META = {
    housekeeping: { icon: BedDouble, accent: 'bg-emerald-500/20 text-emerald-300' },
    security: { icon: ShieldCheck, accent: 'bg-rose-500/20 text-rose-300' },
    concierge: { icon: ConciergeBell, accent: 'bg-amber-400/20 text-amber-300' },
    valet: { icon: CarFront, accent: 'bg-indigo-500/20 text-indigo-300' },
    maintenance: { icon: Wrench, accent: 'bg-orange-500/20 text-orange-300' },
    parking: { icon: CircleParking, accent: 'bg-sky-500/20 text-sky-300' },
    waste_recycling: { icon: Recycle, accent: 'bg-green-500/20 text-green-300' },
    amenities: { icon: Waves, accent: 'bg-cyan-500/20 text-cyan-300' },
    building: { icon: Building2, accent: 'bg-violet-500/20 text-violet-300' },
    all: { icon: ClipboardList, accent: 'bg-sky-600/20 text-sky-300' },
    manager: { icon: BarChart3, accent: 'bg-slate-500/20 text-slate-200' },
};
// Category capabilities that map 1:1 to a work area/queue.
const CATEGORY_AREAS = ['maintenance', 'parking', 'waste_recycling', 'amenities', 'building'];

export default function StaffHub() {
    const { cardId } = useParams();
    const [lang, setLang] = useState(() => {
        try { const v = localStorage.getItem(LANG_KEY); return LANGS.includes(v) ? v : null; } catch { return null; }
    });
    const [phase, setPhase] = useState(lang ? 'pin' : 'language');
    const [hotelName, setHotelName] = useState('');
    const [opsBrand, setOpsBrand] = useState(null);
    const [isHotel, setIsHotel] = useState(true);
    const [pin, setPin] = useState('');
    const [pinError, setPinError] = useState('');
    const [busy, setBusy] = useState(false);
    // Session in MEMORY ONLY — never persisted (shared hallway card safety).
    const [session, setSession] = useState(null);
    const [hasPortalLogin, setHasPortalLogin] = useState(false);
    // Hotel Ops (C3): the room-details tile + live personal work counts.
    const [detailRoom, setDetailRoom] = useState(null);
    const [workCounts, setWorkCounts] = useState(null);
    // Chunk B — Staff PIN lifecycle (self-setup / forgot / secure reset).
    const [pinEmail, setPinEmail] = useState('');
    const [newPin, setNewPin] = useState('');
    const [confirmPin, setConfirmPin] = useState('');
    const [pinMsg, setPinMsg] = useState('');
    const [pinSuccess, setPinSuccess] = useState('');
    const [resetToken, setResetToken] = useState(null);
    const inactivityRef = useRef(null);

    // If the staff arrived via a "reset PIN" email link, open the reset view.
    useEffect(() => {
        try {
            const t = new URLSearchParams(window.location.search).get('pinreset');
            if (t) { setResetToken(t); setPhase('pinreset'); }
        } catch {}
    }, []);

    const tx = STAFF_TEXT[lang] || STAFF_TEXT.en;

    useEffect(() => {
        captureDemoToken(cardId);
        fetch(`${API}/api/assistant/${cardId}/info`)
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => { if (d) { setHotelName(d.hotel_name || ''); setOpsBrand(d.hotel_ops || null); const pt = d.config?.property_type; setIsHotel(!pt || pt === 'hotel'); } })
            .catch(() => {});
        try { setHasPortalLogin(!!getToken()); } catch {}
    }, [cardId]);

    // Hotel demo per-role entry: a staff session was already minted by
    // /demo/hotel/<department>. Hydrate it once (identical shape to the PIN
    // path's response) — no change to capability filtering or layout.
    useEffect(() => {
        let raw = null;
        try { raw = sessionStorage.getItem('yulontap_hotel_demo_staff'); } catch { raw = null; }
        if (!raw) return;
        try {
            const d = JSON.parse(raw);
            if (!d?.session?.token || d.card_id !== cardId) return;
            sessionStorage.removeItem('yulontap_hotel_demo_staff');
            sessionStorage.setItem('yulontap_hotel_demo_staff_active', JSON.stringify({ card_id: d.card_id, department: d.department }));
            startSession(d.session);
            setPhase('greeting');
        } catch { /* ignore malformed payload */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardId]);

    const lock = useCallback((msg) => {
        setSession(null);
        setPin('');
        setPinError(msg);
        setPhase('pin');
    }, []);

    const resetInactivity = useCallback(() => {
        if (inactivityRef.current) clearTimeout(inactivityRef.current);
        inactivityRef.current = setTimeout(() => lock(STAFF_TEXT[lang]?.locked || STAFF_TEXT.en.locked), INACTIVITY_MS);
    }, [lock, lang]);

    useEffect(() => () => { if (inactivityRef.current) clearTimeout(inactivityRef.current); }, []);

    // Where each work area opens to. Housekeeping on a floor card still lands on
    // the floor door grid (preserves existing hotel behavior).
    const openArea = useCallback((area) => {
        if (area === 'housekeeping') { setPhase(session?.floor ? 'floor' : 'housekeeping'); return; }
        setPhase(area);
    }, [session]);

    const startSession = (data) => {
        setSession(data);
        setPin('');
        setPinError('');
        resetInactivity();
    };

    // After a successful auth we go straight to a brief greeting, then the
    // personalized workspace — no pre-auth sector guessing.
    const afterAuth = () => { setPhase('greeting'); };

    // "Done / Switch user" returns the shared card to the PIN screen.
    const resetToPin = () => {
        setSession(null); setPin(''); setPinError('');
        setWorkCounts(null);
        setPhase('pin');
    };

    const clearPinFlow = () => {
        setPinEmail(''); setNewPin(''); setConfirmPin(''); setPinMsg('');
    };

    // Chunk B — first-time self-setup: verify email + create a unique 4-digit PIN.
    const submitPinSetup = async (e) => {
        e.preventDefault();
        if (newPin.length !== 4 || newPin !== confirmPin) { setPinMsg(tx.pinMismatch); return; }
        setBusy(true); setPinMsg('');
        try {
            const r = await fetch(`${API}/api/vouchers/create-pin`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ card_id: cardId, email: pinEmail.trim(), pin: newPin, preferred_language: lang }),
            });
            if (r.ok) { clearPinFlow(); setPinError(''); setPhase('pin'); setPin(''); setPinSuccess(tx.pinCreatedOk); }
            else if (r.status === 409) setPinMsg(tx.pinExists);
            else if (r.status === 404) setPinMsg(tx.emailNotRecognized);
            else setPinMsg(tx.error);
        } catch { setPinMsg(tx.error); } finally { setBusy(false); }
    };

    // Chunk B — forgot PIN: request a secure, time-limited email reset link.
    const submitPinForgot = async (e) => {
        e.preventDefault();
        setBusy(true); setPinMsg('');
        try {
            const r = await fetch(`${API}/api/vouchers/forgot-pin`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ card_id: cardId, email: pinEmail.trim() }),
            });
            if (r.ok) setPinMsg(tx.pinForgotSent);
            else if (r.status === 429) setPinMsg(tx.tooManyRequests);
            else if (r.status === 503) setPinMsg(tx.pinEmailUnavailable);
            else setPinMsg(tx.error);
        } catch { setPinMsg(tx.error); } finally { setBusy(false); }
    };

    // Chunk B — confirm reset link + set a new PIN (old PIN invalidated on success).
    const submitPinReset = async (e) => {
        e.preventDefault();
        if (newPin.length !== 4 || newPin !== confirmPin) { setPinMsg(tx.pinMismatch); return; }
        setBusy(true); setPinMsg('');
        try {
            const r = await fetch(`${API}/api/vouchers/reset-pin`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token: resetToken, pin: newPin }),
            });
            const d = await r.json().catch(() => ({}));
            if (r.ok) {
                clearPinFlow(); setResetToken(null);
                try { window.history.replaceState({}, '', `/assistant/staff/${cardId}`); } catch {}
                setPhase('pin'); setPinSuccess(tx.pinResetOk);
            } else { setPinMsg(d?.detail || tx.pinResetInvalid); }
        } catch { setPinMsg(tx.error); } finally { setBusy(false); }
    };

    const greetingText = () => {
        const h = new Date().getHours();
        const g = h < 12 ? tx.goodMorning : h < 18 ? tx.goodAfternoon : tx.goodEvening;
        return `${g}, ${session?.staff_name || ''}`.trim();
    };

    // Greeting auto-advances into the personalized workspace (hub).
    useEffect(() => {
        if (phase !== 'greeting') return;
        const t = setTimeout(() => setPhase('hub'), 1500);
        return () => clearTimeout(t);
    }, [phase]);

    // Fetch live personal work counts whenever the session is (re)established.
    useEffect(() => {
        if (!session?.token) { setWorkCounts(null); return; }
        let alive = true;
        fetch(`${API}/api/assistant/staff/my-work-summary`, {
            headers: { 'X-Staff-Session': session.token },
        })
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => { if (alive && d) setWorkCounts(d.counts || {}); })
            .catch(() => {});
        return () => { alive = false; };
    }, [session, phase]);

    const handlePinSubmit = async (e) => {
        e.preventDefault();
        if (pin.length !== 4) return;
        setBusy(true);
        setPinError('');
        try {
            const r = await fetch(`${API}/api/assistant/staff/verify`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ card_id: cardId, pin, demo_token: getDemoToken(cardId) }),
            });
            const d = await r.json().catch(() => ({}));
            if (r.ok) { startSession(d); afterAuth(d); }
            else if (d.detail === 'no_hotel_capabilities') setPinError(tx.noCapabilities);
            else setPinError(tx.pinInvalid);
        } catch { setPinError(tx.error); } finally { setBusy(false); }
    };

    const handleAuthContinue = async () => {
        setBusy(true);
        setPinError('');
        try {
            const token = getToken();
            const r = await fetch(`${API}/api/assistant/staff/session-from-auth`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ card_id: cardId }),
            });
            const d = await r.json().catch(() => ({}));
            if (r.ok) { startSession(d); afterAuth(d); }
            else if (d.detail === 'no_hotel_capabilities') setPinError(tx.noCapabilities);
            else setPinError(tx.error);
        } catch { setPinError(tx.error); } finally { setBusy(false); }
    };

    // Authorized API helper — X-Staff-Session header; 401 → lock (re-identify).
    const call = useCallback(async (path, opts = {}) => {
        resetInactivity();
        const r = await fetch(`${API}/api/assistant/staff/${path}`, {
            method: opts.method || 'GET',
            headers: { 'Content-Type': 'application/json', 'X-Staff-Session': session?.token || '' },
            body: opts.body ? JSON.stringify(opts.body) : undefined,
        });
        if (r.status === 401) { lock(tx.locked); throw new Error('locked'); }
        if (!r.ok) throw new Error('request failed');
        return r.json();
    }, [session, lock, resetInactivity, tx.locked]);

    // Like call(), but returns { ok, status, data } instead of throwing on 4xx —
    // used by the room-status workflow which needs the error detail codes.
    const callSafe = useCallback(async (path, opts = {}) => {
        resetInactivity();
        const r = await fetch(`${API}/api/assistant/staff/${path}`, {
            method: opts.method || 'GET',
            headers: { 'Content-Type': 'application/json', 'X-Staff-Session': session?.token || '' },
            body: opts.body ? JSON.stringify(opts.body) : undefined,
        });
        if (r.status === 401) { lock(tx.locked); throw new Error('locked'); }
        const data = await r.json().catch(() => ({}));
        return { ok: r.ok, status: r.status, data };
    }, [session, lock, resetInactivity, tx.locked]);

    // P6: fire-and-forget cleanup of orphaned staff-action photos.
    const cleanupActionPhotos = useCallback((ids) => {
        if (!ids || !ids.length) return;
        callSafe('action-photos/cleanup', { method: 'POST', body: { photo_ids: ids } }).catch(() => {});
    }, [callSafe]);

    // P4: multipart upload for reusable staff-action photos.
    const uploadActionPhotos = useCallback(async (files, meta = {}) => {
        resetInactivity();
        const fd = new FormData();
        fd.append('action_type', meta.action_type || 'staff_action');
        if (meta.room_number) fd.append('room_number', meta.room_number);
        if (meta.note) fd.append('note', meta.note);
        (files || []).forEach((f) => fd.append('photos', f));
        const r = await fetch(`${API}/api/assistant/staff/action-photos`, {
            method: 'POST', headers: { 'X-Staff-Session': session?.token || '' }, body: fd,
        });
        if (r.status === 401) { lock(tx.locked); throw new Error('locked'); }
        const data = await r.json().catch(() => ({}));
        return { ok: r.ok, status: r.status, data };
    }, [session, lock, resetInactivity, tx.locked]);

    const caps = session?.capabilities || [];

    const _opsBg = opsBrand?.background_image_url || '';
    const _opsLogoH = { small: 40, medium: 64, large: 88, very_large: 120 }[opsBrand?.logo_size] || 64;
    const wrap = (children) => (
        <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center p-4 sm:p-6 relative"
            onClick={session ? resetInactivity : undefined} data-testid="hotel-staff-page">
            {_opsBg && (
                <>
                    <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${_opsBg})` }}
                        data-testid="hotel-ops-bg" aria-hidden="true" />
                    <div className="absolute inset-0 bg-slate-950/75" aria-hidden="true" />
                </>
            )}
            <ChangeLanguageLink enabled={LANGS} variant="dark"
                onOpen={() => { try { localStorage.removeItem(LANG_KEY); } catch {} setLang(null); setPhase('language'); }} />
            <div className="w-full max-w-lg mx-auto flex-1 flex flex-col justify-center py-8 relative z-10">
                <div className="text-center mb-6">
                    {opsBrand?.logo_url && (
                        <img src={opsBrand.logo_url} alt="" className="mx-auto mb-3 object-contain"
                            style={{ maxHeight: _opsLogoH }} data-testid="hotel-ops-logo"
                            onError={(e) => { e.target.style.display = 'none'; }} />
                    )}
                    <p className="text-emerald-400/70 text-xs uppercase tracking-widest" data-testid="staff-hotel-name">{hotelName}</p>
                    <h1 className="text-2xl sm:text-3xl font-bold text-white mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {isHotel ? tx.staffTitle : tx.staffPortalTitle}
                    </h1>
                    {session?.floor && (
                        <p className="text-emerald-300 text-sm font-semibold mt-1" data-testid="staff-floor-badge">
                            {tx.floor} {session.floor}
                        </p>
                    )}
                </div>
                {children}
            </div>
            {detailRoom && (
                <RoomDetailsPanel roomNumber={detailRoom} callSafe={callSafe} tx={tx}
                    onClose={() => setDetailRoom(null)} />
            )}
        </div>
    );

    if (phase === 'language') return wrap(
        <LanguageGate enabled={LANGS} variant="dark" embedded
            onSelect={(code) => {
                setLang(code);
                try { localStorage.setItem(LANG_KEY, code); } catch {}
                setPhase('pin');
            }} />
    );

    if (phase === 'greeting') return wrap(
        <div className="text-center" data-testid="staff-greeting">
            <p className="text-3xl sm:text-4xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}
                data-testid="staff-greeting-text">{greetingText()}</p>
            <button type="button" onClick={() => setPhase('hub')}
                className="mt-6 w-full py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors"
                data-testid="staff-greeting-continue">
                {tx.continueBtn}
            </button>
        </div>
    );

    if (phase === 'pin') return wrap(
        <form onSubmit={handlePinSubmit} className="bg-white/5 border border-white/10 rounded-2xl p-6 max-w-md mx-auto w-full" data-testid="staff-pin-form">
            <div className="flex items-center gap-2 mb-1">
                <LockKeyhole className="w-4 h-4 text-emerald-400" />
                <p className="text-white font-semibold">{isHotel ? tx.pinTitle : tx.pinTitleGeneric}</p>
            </div>
            <p className="text-slate-400 text-sm mb-4">{tx.pinPrompt}</p>
            {pinSuccess && (
                <p className="text-emerald-300 text-sm mb-3 text-center bg-emerald-950/40 border border-emerald-500/30 rounded-lg px-3 py-2"
                    data-testid="staff-pin-success">{pinSuccess}</p>
            )}
            <input type="password" inputMode="numeric" autoComplete="off" maxLength={4}
                value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                placeholder="••••"
                className="w-full rounded-xl bg-slate-900/60 border border-white/15 text-white text-2xl tracking-[0.5em] text-center py-3 px-4 placeholder:text-slate-600 focus:outline-none focus:border-emerald-500"
                data-testid="staff-pin-input" />
            {pinError && <p className="text-rose-400 text-sm mt-2 text-center" data-testid="staff-pin-error">{pinError}</p>}
            <button type="submit" disabled={pin.length !== 4 || busy}
                className="mt-4 w-full py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors disabled:opacity-50"
                data-testid="staff-pin-submit">
                {busy ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : tx.verify}
            </button>
            {hasPortalLogin && (
                <button type="button" onClick={handleAuthContinue} disabled={busy}
                    className="mt-3 w-full py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-slate-200 text-sm font-medium transition-colors disabled:opacity-50"
                    data-testid="staff-continue-auth-btn">
                    {tx.continueAs}
                </button>
            )}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                <button type="button" onClick={() => { clearPinFlow(); setPinSuccess(''); setPhase('pinsetup'); }}
                    className="text-xs text-emerald-300 hover:text-emerald-200 font-medium transition-colors"
                    data-testid="staff-pin-setup-link">{tx.firstTimeSetup}</button>
                <button type="button" onClick={() => { clearPinFlow(); setPinSuccess(''); setPhase('pinforgot'); }}
                    className="text-xs text-slate-400 hover:text-white transition-colors"
                    data-testid="staff-pin-forgot-link">{tx.forgotPin}</button>
            </div>
        </form>
    );

    if (phase === 'pinsetup') return wrap(
        <form onSubmit={submitPinSetup} className="bg-white/5 border border-white/10 rounded-2xl p-6 max-w-md mx-auto w-full" data-testid="staff-pinsetup-form">
            <button type="button" onClick={() => { clearPinFlow(); setPhase('pin'); }}
                className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white transition-colors mb-3"
                data-testid="staff-pinsetup-back"><ChevronLeft className="w-4 h-4" /> {tx.back}</button>
            <p className="text-white font-semibold mb-1">{tx.setupTitle}</p>
            <p className="text-slate-400 text-sm mb-4">{tx.setupPrompt}</p>
            <input type="email" autoComplete="email" value={pinEmail} onChange={(e) => setPinEmail(e.target.value)}
                placeholder={tx.emailPlaceholder}
                className="w-full rounded-xl bg-slate-900/60 border border-white/15 text-white py-3 px-4 mb-3 placeholder:text-slate-600 focus:outline-none focus:border-emerald-500"
                data-testid="staff-pinsetup-email" />
            <input type="password" inputMode="numeric" maxLength={4} value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
                placeholder={tx.newPinPlaceholder}
                className="w-full rounded-xl bg-slate-900/60 border border-white/15 text-white text-2xl tracking-[0.5em] text-center py-3 px-4 mb-3 placeholder:text-slate-600 focus:outline-none focus:border-emerald-500"
                data-testid="staff-pinsetup-pin" />
            <input type="password" inputMode="numeric" maxLength={4} value={confirmPin} onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                placeholder={tx.confirmPinPlaceholder}
                className="w-full rounded-xl bg-slate-900/60 border border-white/15 text-white text-2xl tracking-[0.5em] text-center py-3 px-4 placeholder:text-slate-600 focus:outline-none focus:border-emerald-500"
                data-testid="staff-pinsetup-confirm" />
            {pinMsg && <p className="text-rose-400 text-sm mt-2 text-center" data-testid="staff-pinsetup-msg">{pinMsg}</p>}
            <button type="submit" disabled={busy || !pinEmail || newPin.length !== 4 || confirmPin.length !== 4}
                className="mt-4 w-full py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors disabled:opacity-50"
                data-testid="staff-pinsetup-submit">{busy ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : tx.createPin}</button>
        </form>
    );

    if (phase === 'pinforgot') return wrap(
        <form onSubmit={submitPinForgot} className="bg-white/5 border border-white/10 rounded-2xl p-6 max-w-md mx-auto w-full" data-testid="staff-pinforgot-form">
            <button type="button" onClick={() => { clearPinFlow(); setPhase('pin'); }}
                className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white transition-colors mb-3"
                data-testid="staff-pinforgot-back"><ChevronLeft className="w-4 h-4" /> {tx.back}</button>
            <p className="text-white font-semibold mb-1">{tx.forgotTitle}</p>
            <p className="text-slate-400 text-sm mb-4">{tx.forgotPrompt}</p>
            <input type="email" autoComplete="email" value={pinEmail} onChange={(e) => setPinEmail(e.target.value)}
                placeholder={tx.emailPlaceholder}
                className="w-full rounded-xl bg-slate-900/60 border border-white/15 text-white py-3 px-4 placeholder:text-slate-600 focus:outline-none focus:border-emerald-500"
                data-testid="staff-pinforgot-email" />
            {pinMsg && <p className="text-emerald-300 text-sm mt-3 text-center bg-emerald-950/30 border border-emerald-500/20 rounded-lg px-3 py-2" data-testid="staff-pinforgot-msg">{pinMsg}</p>}
            <button type="submit" disabled={busy || !pinEmail}
                className="mt-4 w-full py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors disabled:opacity-50"
                data-testid="staff-pinforgot-submit">{busy ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : tx.sendResetLink}</button>
        </form>
    );

    if (phase === 'pinreset') return wrap(
        <form onSubmit={submitPinReset} className="bg-white/5 border border-white/10 rounded-2xl p-6 max-w-md mx-auto w-full" data-testid="staff-pinreset-form">
            <p className="text-white font-semibold mb-1">{tx.resetTitle}</p>
            <p className="text-slate-400 text-sm mb-4">{tx.resetPrompt}</p>
            <input type="password" inputMode="numeric" maxLength={4} value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
                placeholder={tx.newPinPlaceholder}
                className="w-full rounded-xl bg-slate-900/60 border border-white/15 text-white text-2xl tracking-[0.5em] text-center py-3 px-4 mb-3 placeholder:text-slate-600 focus:outline-none focus:border-emerald-500"
                data-testid="staff-pinreset-pin" />
            <input type="password" inputMode="numeric" maxLength={4} value={confirmPin} onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                placeholder={tx.confirmPinPlaceholder}
                className="w-full rounded-xl bg-slate-900/60 border border-white/15 text-white text-2xl tracking-[0.5em] text-center py-3 px-4 placeholder:text-slate-600 focus:outline-none focus:border-emerald-500"
                data-testid="staff-pinreset-confirm" />
            {pinMsg && <p className="text-rose-400 text-sm mt-2 text-center" data-testid="staff-pinreset-msg">{pinMsg}</p>}
            <button type="submit" disabled={busy || newPin.length !== 4 || confirmPin.length !== 4}
                className="mt-4 w-full py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors disabled:opacity-50"
                data-testid="staff-pinreset-submit">{busy ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : tx.setNewPin}</button>
        </form>
    );

    if (phase === 'hub') {
        const meta = (k) => AREA_META[k] || AREA_META.all;
        const areaList = [];
        if (caps.includes('housekeeping')) areaList.push({ key: 'housekeeping', title: tx.housekeeping, count: workCounts?.housekeeping });
        if (caps.includes('security')) areaList.push({ key: 'security', title: tx.security, count: workCounts?.security });
        if (caps.includes('concierge')) areaList.push({ key: 'concierge', title: tx.concierge, count: workCounts?.concierge });
        CATEGORY_AREAS.forEach((c) => { if (caps.includes(c)) areaList.push({ key: c, title: tx.cat?.[c] || c, count: workCounts?.[c] }); });
        if (caps.includes('valet')) areaList.push({ key: 'valet', title: tx.valet, count: workCounts?.valet });
        if (caps.includes('management') || caps.includes('hotel_manager')) areaList.push({ key: 'all', title: tx.allWork, count: workCounts?.all });
        if (caps.includes('hotel_manager')) areaList.push({ key: 'manager', title: tx.opsSummaryTitle, noCount: true });

        return wrap(
            <div data-testid="staff-hub">
                <div className="flex items-center justify-between mb-3">
                    <p className="text-slate-300 text-sm" data-testid="staff-identity">
                        {tx.hello}, <span className="text-white font-semibold">{session?.staff_name}</span>
                    </p>
                    <button type="button" onClick={resetToPin}
                        className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-full transition-colors"
                        data-testid="staff-switch-user-btn">
                        <LogOut className="w-3.5 h-3.5" /> {tx.switchUser}
                    </button>
                </div>
                <p className="text-white font-semibold text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>{tx.hubTitle}</p>
                <p className="text-slate-400 text-sm mb-4" data-testid="staff-hub-intro">{tx.hubIntro}</p>
                <div className="space-y-3" data-testid="staff-capability-menu">
                    {areaList.map((a) => {
                        const M = meta(a.key);
                        const Icon = M.icon;
                        const has = typeof a.count === 'number' && a.count > 0;
                        return (
                            <button key={a.key} type="button" onClick={() => openArea(a.key)}
                                className="group w-full flex items-center gap-4 rounded-2xl bg-white/[0.05] border border-white/10 p-4 text-left transition-all duration-200 hover:bg-white/[0.09] hover:-translate-y-0.5"
                                data-testid={`staff-cap-${a.key}`}>
                                <span className={`shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${M.accent}`}>
                                    <Icon className="w-6 h-6" strokeWidth={1.75} />
                                </span>
                                <span className="min-w-0 flex-1">
                                    <span className="block text-white font-semibold text-base">{a.title}</span>
                                    <span className="block text-slate-400 text-xs mt-0.5">{tx.areaDesc?.[a.key]}</span>
                                </span>
                                {!a.noCount && (
                                    <span className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full ${has ? 'bg-emerald-600 text-white' : 'bg-white/10 text-slate-400'}`}
                                        data-testid={`staff-count-${a.key}`}>
                                        {has ? `${a.count} ${tx.notifSuffix}` : tx.allClear}
                                    </span>
                                )}
                                <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-white transition-colors shrink-0" />
                            </button>
                        );
                    })}
                </div>
                {/* Recent actions — this person's own history (shared platform panel) */}
                <div className="mt-4">
                    <ActionHistoryPanel
                        testid="staff-action-history"
                        api={{
                            list: () => actionHistoryAPI.list({ staffToken: session?.token }),
                            dismiss: (ids) => actionHistoryAPI.dismiss({ staffToken: session?.token }, ids),
                            clear: () => actionHistoryAPI.clear({ staffToken: session?.token }),
                        }}
                        labels={{
                            title: tx.ahTitle, empty: tx.ahEmpty, clearAll: tx.ahClearAll,
                            remove: tx.ahRemove, footnote: tx.ahFootnote,
                            action: { acknowledge: tx.ahAcknowledge, resolve: tx.ahResolve, cancel: tx.ahCancel },
                        }}
                    />
                </div>
            </div>
        );
    }

    const backToHub = () => setPhase('hub');

    const CAT_KINDS = ['maintenance', 'parking', 'waste_recycling', 'amenities', 'building', 'all'];
    if (CAT_KINDS.includes(phase)) return wrap(<QueueView tx={tx} kind={phase} titleOverride={phase === 'all' ? (tx.allWork || 'All Work') : (tx.cat?.[phase] || phase)} call={call} onBack={backToHub} photoToken={session?.token} api={API} />);

    if (phase === 'floor') return wrap(<FloorView tx={tx} call={call} session={session} onBack={backToHub} onOpenTools={backToHub} onUpdateRoom={() => setPhase('roomstatus')} onOpenRoom={setDetailRoom} />);
    if (phase === 'roomstatus') return wrap(<RoomStatusView tx={tx} callSafe={callSafe} uploadActionPhotos={uploadActionPhotos} cleanupActionPhotos={cleanupActionPhotos} session={session} onBack={() => setPhase('floor')} />);
    if (phase === 'housekeeping') return wrap(<HousekeepingView tx={tx} call={call} onBack={backToHub} onOpenRoom={setDetailRoom} onOpenRequests={() => setPhase('housekeeping_requests')} />);
    if (phase === 'housekeeping_requests') return wrap(<QueueView tx={tx} kind="housekeeping" titleOverride={tx.hkRequests} call={call} onBack={() => setPhase('housekeeping')} photoToken={session?.token} api={API} />);
    if (phase === 'security') return wrap(<QueueView tx={tx} kind="security" call={call} onBack={backToHub} photoToken={session?.token} api={API} />);
    if (phase === 'concierge') return wrap(<QueueView tx={tx} kind="concierge" call={call} onBack={backToHub} photoToken={session?.token} api={API} />);
    if (phase === 'valet') return wrap(<ValetView tx={tx} call={call} onBack={backToHub} photoToken={session?.token} api={API} />);
    if (phase === 'manager') return wrap(<ManagerView tx={tx} call={call} onBack={backToHub} onOpenSector={(k) => setPhase(k)} isHotel={isHotel} />);

    return null;
}
